import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ShieldCheck, FileText, Lock, HeartHandshake, Users, Sparkles, ArrowRight, ArrowLeft, HelpCircle, BadgeCheck } from "lucide-react";

const TOPICS = {
  "how-it-works": {
    icon: <BadgeCheck className="w-6 h-6" />,
    eyebrow: "The basics",
    title: "How MyTableTopApp works",
    intro: "Three simple steps — with a parent-approval gate at the centre, so nothing goes live without a grown-up's OK.",
    sections: [
      { h: "1. Kids create a listing", p: "Toddlers to teens photograph a toy they've outgrown, add a friendly description, and pick a fair price. Younger kids use big buttons and voice-to-text; older kids get a proper listing editor." },
      { h: "2. Parents approve", p: "Every listing lands in the parent's Moderation Queue. Nothing appears on the public marketplace until you tap Approve — you can edit copy, adjust the price, or send it back with a note." },
      { h: "3. Secure checkout & payout", p: "Buyers pay via Stripe. A 10% Family Safety & Buyer Protection Fee is added to the buyer's total (funds identity checks, AI moderation, and buyer protection). The full item price is held in the family Wallet until the item ships and the buyer confirms — then payout to your linked bank." },
    ],
    cta: { href: "/browse", label: "Browse the marketplace" },
  },
  about: {
    icon: <Users className="w-6 h-6" />,
    eyebrow: "Our story",
    title: "About MyTableTopApp",
    intro: "A Hackney Born app that turns Used - Good Condition toys into a first taste of real-world business, finance, and sustainability.",
    sections: [
      { h: "Co-designed with real kids", p: "We built MyTableTopApp alongside our Year 4 to Year 6 coding club students from Holmleigh, Our Lady, and Shoreditch Park Primary schools. Every screen was shaped by kids telling us what a 'real' shop should look and feel like." },
      { h: "Four pillars for young entrepreneurs", p: "Financial literacy · e-commerce & marketing · customer service & negotiation · a green initiative that keeps toys out of landfill and money in family pockets." },
      { h: "Safety before everything", p: "Preset-only messaging, an automated family-safety review on every listing, a PIN-gated Parent Zone, and full UK GDPR (Data Protection) compliance mean parents stay firmly in the loop from listing to payout." },
    ],
    cta: { href: "/?waitlist=1", label: "Join the app-store waitlist" },
  },
  privacy: {
    icon: <Lock className="w-6 h-6" />,
    eyebrow: "Legal",
    title: "Privacy Policy",
    updated: "6 August 2026",
    intro: "We hold the smallest amount of personal data we possibly can, we never sell it, and we're fully UK GDPR (Data Protection) compliant.",
    sections: [
      { h: "What we collect", p: "Parent account: email, name, mobile, and hashed password. Kid profile: nickname, age bracket (4–6, 7–10, 11–13, 14–16), and an emoji avatar — never a photo of the child. Listings: photos of the toy itself, description, price. Waitlist: your email + which store you want us to notify you about." },
      { h: "How we use it", p: "To run the marketplace: authenticate you, show your parent moderation queue, process payments (via Stripe), and notify you about your orders. Analytics are aggregate-only. We do not sell, rent, or share data with advertisers." },
      { h: "Payment processing & parent payouts (Stripe Connect)", p: (
        <>
          We use <strong>Stripe</strong> to process wallet top-ups and bank payouts. For identity verification (KYC/AML), bank validation, and payout processing, <strong>Stripe operates as an independent Data Controller</strong> for personal and financial data. Learn more at{" "}
          <a
            href="https://stripe.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brandTeal font-semibold underline hover:no-underline"
            data-testid="privacy-stripe-link"
          >
            stripe.com/privacy
          </a>.
        </>
      ) },
      { h: "Children's data", p: "Under 13s cannot create an account directly — every profile is opened and managed by a verified parent account. We collect no birthdays, no location, no device identifiers beyond a minimal session cookie." },
      { h: "Your rights", p: "Use our secure contact form to request a copy of your data, correct any of it, or delete your account entirely. We respond within 14 days." },
    ],
    cta: { href: "/contact?topic=account_query", label: "Contact our privacy team" },
  },
  terms: {
    icon: <FileText className="w-6 h-6" />,
    eyebrow: "Legal",
    title: "Terms & Conditions",
    updated: "6 August 2026",
    intro: "The house rules for using MyTableTopApp. Plain English, no gotchas.",
    sections: [
      { h: "Who can use MyTableTopApp", p: "Parent accounts must be 18+ and legally responsible for the child(ren) they add. Kids under 13 use the platform only under a parent account. Sellers must own the item they list and have permission from their parent to sell it." },
      { h: "Fees", p: "Signing up is free. Sellers keep 100% of the item list price. Buyers pay a 10% Family Safety & Buyer Protection Fee on top of the item price, plus Stripe processing on external card checkouts. Fees are itemised at checkout and in your Wallet. No hidden charges." },
      { h: "Payment & payout services", p: (
        <>
          Wallet top-ups and parent payouts are executed via <strong>Stripe</strong>. By using these features, you acknowledge that Stripe handles identity verification, payment processing, and payout management, acting as an <strong>independent Data Controller</strong> for compliance (KYC/AML) purposes. See our{" "}
          <Link to="/info/privacy" className="text-brandTeal font-semibold underline hover:no-underline" data-testid="terms-privacy-link">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <a
            href="https://stripe.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brandTeal font-semibold underline hover:no-underline"
            data-testid="terms-stripe-link"
          >
            stripe.com/privacy
          </a>{" "}
          for more.
        </>
      ) },
      { h: "Prohibited items", p: "No food, weapons, digital accounts, live animals, tobacco, alcohol, or anything that would be inappropriate for a child. Our automated safety review and parent-approval queue catch most of this before it goes live." },
      { h: "Refunds & disputes", p: "Buyers can raise a dispute within 7 days of delivery via the order page. We hold funds in escrow until either side confirms or a resolution is reached." },
      { h: "Account termination", p: "We may suspend accounts that break these terms. You can delete your account at any time from the Parent Dashboard." },
    ],
    cta: { href: "/contact?topic=general_feedback", label: "Question about our terms?" },
  },
  safety: {
    icon: <ShieldCheck className="w-6 h-6" />,
    eyebrow: "Trust & safety",
    title: "Parental Safety Guidelines",
    intro: "How we keep every child safe on MyTableTopApp — and how you can add your own layer on top.",
    sections: [
      { h: "Preset-only messaging", p: "Children never type free-form messages to strangers. Communication uses a fixed set of parent-approved presets (\"Is it still available?\", \"Thank you!\", etc.) so bullying, grooming, or personal-detail leaks are structurally impossible." },
      { h: "PIN Parent Zone", p: "Every sensitive action — approving a listing, editing payout details, changing a kid's age bracket, releasing funds — is gated behind your 4-digit PIN. Set or change it any time at /pin." },
      { h: "Automated safety review", p: "Every listing photo and description passes through an automated safety review that flags anything unsuitable before you even see it in your Moderation Queue — so parents only spend time on the borderline cases." },
      { h: "UK GDPR & Data Protection", p: "We never collect a child's real name, birthday, exact location, or contact details. Avatars are emoji-only. Session cookies are the sole client-side identifier." },
      { h: "Reporting", p: "Something feels off? Use our contact form with the listing ID or order ID — we respond within 24 hours, faster for anything that mentions a child's safety." },
    ],
    cta: { href: "/contact?topic=order_issue", label: "Report a safety concern" },
  },
  faqs: {
    icon: <HelpCircle className="w-6 h-6" />,
    eyebrow: "Help",
    title: "Frequently Asked Questions",
    intro: "The most common questions from parents and kids getting started with MyTableTopApp.",
    sections: [
      { h: "Do I need to install an app?", p: "Not yet — the mobile apps are coming to the App Store and Google Play. In the meantime, the full experience runs in your browser at mytabletopapp.co.uk. Tap 'Launch Web App' anywhere on the landing page to jump in." },
      { h: "How old does my child need to be?", p: "There's an age bracket for every stage: 4–6 (big buttons, visual only), 7–10 and 11–13 (guided listing with parent approval), and 14–16 (optional independent banking with parent override). Everyone under 18 uses a profile inside a parent account." },
      { h: "What does it cost?", p: "Sign-up is free. When a sale completes, we take £0.30 + 5% plus Stripe processing — itemised at checkout. No listing fees, no subscription." },
      { h: "How do payouts work?", p: "Sales land in a family Wallet on your Parent Dashboard. Once the buyer confirms delivery (or 7 days pass with no dispute), the funds unlock and you can withdraw to your linked bank via Stripe." },
      { h: "Is my child's chat safe?", p: "Kids never type free-form messages to buyers. All communication uses parent-approved preset phrases — grooming, bullying, or contact-detail leaks are structurally impossible." },
      { h: "Can I remove a listing after it's live?", p: "Yes. From your Parent Dashboard's Moderation column, tap any live listing and hit 'Unlist'. It vanishes from the marketplace instantly." },
      { h: "Which countries can I sell in?", p: "We're UK-only at launch. All prices are in GBP and shipping is domestic Royal Mail / Evri." },
    ],
    cta: { href: "/contact?topic=general_feedback", label: "Ask us anything" },
  },
};

export default function Info() {
  const { topic } = useParams();
  const data = TOPICS[topic];

  if (!data) return <Navigate to="/" replace />;

  const isExternal = data.cta.href.startsWith("mailto:") || data.cta.href.startsWith("http");

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid={`info-page-${topic}`}>
        <Link
          to="/info"
          className="inline-flex items-center gap-1 text-sm font-semibold text-brandTeal hover:text-brandTealDark"
          data-testid={`info-back-top-${topic}`}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Information Hub
        </Link>

        <div className="mt-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-brandTeal/15 text-brandTeal px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em]">
            <ShieldCheck className="w-3.5 h-3.5" /> {data.eyebrow}
          </div>
          <div className="mt-5 flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brandPink/15 text-brandPink">
              {data.icon}
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-brandSlate" data-testid={`info-title-${topic}`}>
              {data.title}
            </h1>
          </div>
          <p className="text-lg text-slate-600 mt-5 leading-relaxed">{data.intro}</p>
          {data.updated && (
            <div
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-600 px-3 py-1 text-xs font-semibold"
              data-testid={`info-updated-${topic}`}
            >
              <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" /> Last updated: {data.updated}
            </div>
          )}
        </div>

        <div className="mt-10 space-y-6">
          {data.sections.map((s, i) => (
            <div
              key={i}
              id={`section-${i}`}
              className="card-base p-6 md:p-7 scroll-mt-24"
              data-testid={`info-section-${topic}-${i}`}
            >
              <h2 className="font-display text-xl font-semibold text-brandSlate">{s.h}</h2>
              <div className="text-slate-600 mt-2 leading-relaxed">{s.p}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3 items-center">
          {isExternal ? (
            <a href={data.cta.href} className="btn-primary" data-testid={`info-cta-${topic}`}>
              {data.cta.label} <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <Link to={data.cta.href} className="btn-primary" data-testid={`info-cta-${topic}`}>
              {data.cta.label} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <Link
            to="/info"
            className="btn-secondary"
            data-testid={`info-back-bottom-${topic}`}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Information Hub
          </Link>
          <Link to="/" className="text-sm font-semibold text-slate-500 hover:text-brandSlate transition" data-testid={`info-back-home-${topic}`}>
            Or go to homepage
          </Link>
        </div>

        <div className="mt-10 text-xs text-slate-400" data-testid={`info-contact-hint-${topic}`}>
          Last updated: February 2026 · Questions? <Link to="/contact" className="text-brandTeal hover:underline">Get in touch via our contact form</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
